import React from 'react'
import { J1LeftarrowD } from '@e3dc-react/icons'
import { Box, Card, CardMedia, IconButton, Typography } from '@mui/material'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper'
import { makeStyles } from 'tss-react/mui'
import { ProductResponse } from 'src/Libs/shop/models'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import MoreInfoProductCategories from '../MoreInfoProductCategories'
import NoImage from 'src/Resources/Images/shop/NoImage.png'
import { getImageUrlBySize } from 'src/Libs/shop/utils'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'

interface RetrofitBatteryDetailsProps {
  onUserExitDetails: () => void
  detailsData: ProductResponse
}

const useStyles = makeStyles()(theme => ({
  image: {
    display: 'block',
    width: '100%',
    minHeight: '400px',
    objectFit: 'cover',
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '--swiper-theme-color': theme.palette.grey.grey09,
    '.swiper-pagination-bullet-active': {
      transform: 'scale(1.5)',
    },
  },
  exitIcon: {
    color: theme.palette.grey.grey04,
    width: 32,
    height: 32,
    transform: 'translateX(-30px) translateY(-25px)',
    zIndex: 1,
    position: 'absolute',
  },
}))

const RetrofitBatteryDetails: React.FC<RetrofitBatteryDetailsProps> = ({ onUserExitDetails, detailsData }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  const unitPrice = detailsData?.product.calculatedPrice.unitPrice ?? 0
  const price = detailsData?.product?.calculatedPrice?.listPrice?.price ?? 0
  const productImages = detailsData?.product?.media
  return (
    <Box>
      <IconButton className={classes.exitIcon}>
        <J1LeftarrowD
          onClick={() => {
            onUserExitDetails()
          }}
        />
      </IconButton>
      <Box sx={{ marginTop: '12px' }}>
        {detailsData.product.manufacturer?.translated && (
          <Typography variant="h5" color="primary">
            {detailsData.product.manufacturer.translated.name}
          </Typography>
        )}
        {detailsData.product?.translated && <Typography variant="h1">{detailsData.product.translated.name}</Typography>}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">{t('shop.product.number') + detailsData.product.productNumber}</Typography>
          <div>
            <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
              {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
            </Typography>
            {unitPrice < price && (
              <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                {currencyFormatter(price)}
              </Typography>
            )}
          </div>
        </Box>
        <Swiper
          pagination={{
            clickable: true,
          }}
          loop
          navigation={true}
          spaceBetween={30}
          modules={[Navigation, Pagination]}
          className={classes.imageContainer}
        >
          {productImages !== null ? (
            <>
              {productImages
                .sort((a, b) => a.position - b.position)
                .map(image => {
                  const productImageUrl = getImageUrlBySize(image?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
                  return (
                    <SwiperSlide key={image.position} className={classes.image}>
                      <Card sx={{ minHeight: '390px', my: 1, padding: 0 }}>
                        <CardMedia className={classes.image} component="img" height="194" image={productImageUrl} alt="System konfigurieren" />
                      </Card>
                    </SwiperSlide>
                  )
                })}
            </>
          ) : (
            <SwiperSlide className={classes.image}>
              <Card sx={{ minHeight: '390px', my: 1, padding: 0 }}>
                <CardMedia className={classes.image} component="img" height="194" image={NoImage} alt="System konfigurieren" />
              </Card>
            </SwiperSlide>
          )}
        </Swiper>
      </Box>
      <MoreInfoProductCategories
        productDescription={detailsData.product.translated?.description}
        width={detailsData.product.width}
        height={detailsData.product.height}
        length={detailsData.product.length}
        weight={detailsData.product.weight}
        properties={detailsData.product.properties}
        documents={detailsData.product.extensions.downloads.downloads}
      />
    </Box>
  )
}

export default RetrofitBatteryDetails
